import React from "react";
import {
    Show,
    TextField,
    DateField,
    TabbedShowLayout,
    Tab
} from "react-admin";
import OrgTitle from "./fields/orgTitle.component";
import OrderedProviderRoles from "./fields/orderedProviderRoles.component";
import OrderedUsStates from "./fields/orderedUsStates.component";
import { DATE_TIME_OPTIONS } from "../../app/app.component";
import DefaultPlaceholderField from "../../material/defPlaceholderField.component";
import { orgsFields } from '../orgs.model';
import YesNoField from "../../material/yesNoField.component";
import MedicaidMemberListUpload from "./MedicaidMemberListUpload.component";
import EnrollmentMethodField from "./fields/enrollmentMethod.component";
import InsurancePlanField from "./fields/healthieInsurance.component";
import { SectionedShowLayout, LabeledField } from "../../components/layouts";
import ProductFeatureToggles from './productFeatureToggles.component';
import OrganizationHealthiePatientsTab from './OrganizationHealthiePatientsTab.component';
import OrganizationHealthieProvidersTab from './OrganizationHealthieProvidersTab.component';
import OrganizationClientPackagesTab from './OrganizationClientPackagesTab.component';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { FeatureFlags } from '../../constants/featureFlags';

const OrgShow = (props) => {
    const growthbook = useGrowthBook();
    const showProductFeatures = growthbook && growthbook.isOn(FeatureFlags.ENABLE_ADMIN_ORGANIZATION_FEATURE_TOGGLES);
    const showHealthieData = growthbook && growthbook.isOn(FeatureFlags.ENABLE_ADMIN_PANEL_HEALTHIE_ENRICHMENT);

    const sections = [
        {
            title: "Basic Information",
            fields: [
                <LabeledField key="orgName" label="Name">
                    <DefaultPlaceholderField
                        source={orgsFields.orgName}
                        wrapField={<TextField source={orgsFields.orgName} />}
                    />
                </LabeledField>,
                <LabeledField key="enrollmentMethod" label="Enrollment Method">
                    <DefaultPlaceholderField
                        source={orgsFields.enrollmentMethod}
                        wrapField={<EnrollmentMethodField />}
                    />
                </LabeledField>,
                <LabeledField key="payerId" label="Payer ID">
                    <DefaultPlaceholderField
                        source={orgsFields.payerId}
                        wrapField={<TextField source={orgsFields.payerId} />}
                    />
                </LabeledField>,
                <LabeledField key="healthieInsurance" label="Healthie Insurance">
                    <DefaultPlaceholderField
                        source={orgsFields.healthieInsuranceId}
                        wrapField={<InsurancePlanField />}
                    />
                </LabeledField>,
                <LabeledField key="clientGroupId" label="Client Group ID">
                    <DefaultPlaceholderField
                        source={orgsFields.clientGroupId}
                        wrapField={<TextField source={orgsFields.clientGroupId} />}
                    />
                </LabeledField>,
                <LabeledField key="nursePhone" label="Nurse Phone">
                    <DefaultPlaceholderField
                        source={orgsFields.orgNursePhone}
                        wrapField={<TextField source={orgsFields.orgNursePhone} />}
                    />
                </LabeledField>
            ]
        },
        {
            title: "Feature Settings",
            fields: [
                <LabeledField key="contentLibrary" label="Content Library Enabled">
                    <YesNoField
                        source={orgsFields.contentLibraryEnabled}
                    />
                </LabeledField>,
                <LabeledField key="medicaidId" label="Medicaid ID Registration Question Enabled">
                    <YesNoField
                        source={orgsFields.medicaidIdRegistrationQuestionEnabled}
                    />
                </LabeledField>,
                <LabeledField key="enrollmentCode" label="Enrollment Code Required">
                    <YesNoField
                        source={orgsFields.enrollmentCodeRequired}
                    />
                </LabeledField>,
                <LabeledField key="chatEnabled" label="Chat Enabled">
                    <YesNoField
                        source={orgsFields.chatEnabled}
                    />
                </LabeledField>,
                <LabeledField key="wicId" label="WIC ID Required">
                    <YesNoField
                        source={orgsFields.requiresWicId}
                    />
                </LabeledField>,
                <LabeledField key="isActive" label="Active">
                    <YesNoField
                        source={orgsFields.isActive}
                    />
                </LabeledField>
            ]
        },
        {
            title: "System Information",
            fields: [
                <LabeledField key="createdAt" label="Created At">
                    <DefaultPlaceholderField
                        source={orgsFields.orgCreatedAt}
                        wrapField={<DateField source={orgsFields.orgCreatedAt} options={DATE_TIME_OPTIONS} />}
                    />
                </LabeledField>,
                <LabeledField key="updatedAt" label="Updated At">
                    <DefaultPlaceholderField
                        source={orgsFields.orgUpdatedAt}
                        wrapField={<DateField source={orgsFields.orgUpdatedAt} options={DATE_TIME_OPTIONS} />}
                    />
                </LabeledField>
            ]
        },
        {
            title: "US States",
            fields: [
                <LabeledField key="usStates" label="Supported US States">
                    <OrderedUsStates />
                </LabeledField>
            ]
        },
        {
            title: "Provider Roles",
            fields: [
                <LabeledField key="providerRoles" label="Supported Provider Roles">
                    <OrderedProviderRoles />
                </LabeledField>
            ]
        },
        {
            title: "Medicaid Upload",
            fields: [
                <LabeledField key="medicaidUpload" label="Medicaid Member List Upload">
                    <MedicaidMemberListUpload />
                </LabeledField>
            ]
        }
    ];

    // Common query options to force fresh data fetch for enrichment
    const queryOptions = {
        refetchOnMount: 'always',  // Always fetch fresh data when navigating to show page
        staleTime: 0  // Consider data stale immediately to force refetch with enrichment
    };

    // If neither feature flag is enabled, use original 3-column layout
    if (!showProductFeatures && !showHealthieData) {
        return (
            <Show {...props} title={<OrgTitle />} queryOptions={queryOptions}>
                <SectionedShowLayout sections={sections} />
            </Show>
        );
    }

    // If feature flags are enabled, add tabs
    return (
        <Show {...props} title={<OrgTitle />} queryOptions={queryOptions}>
            <TabbedShowLayout syncWithLocation={false}>
                <Tab label="Details" path="details">
                    <SectionedShowLayout sections={sections} />
                </Tab>
                {showProductFeatures && (
                    <Tab label="Product Features" path="product-features">
                        <ProductFeatureToggles />
                    </Tab>
                )}
                {showHealthieData && (
                    <Tab label="Healthie Patients" path="healthie-patients">
                        <OrganizationHealthiePatientsTab />
                    </Tab>
                )}
                       {showHealthieData && (
                           <Tab label="Pacify Providers" path="pacify-providers">
                               <OrganizationHealthieProvidersTab />
                           </Tab>
                       )}
                       {showHealthieData && (
                           <Tab label="Client Packages" path="client-packages">
                               <OrganizationClientPackagesTab />
                           </Tab>
                       )}
            </TabbedShowLayout>
        </Show>
    );
};

export default OrgShow;
